import { Question } from '../../types';
import {
    CourseDirection,
    CourseForWhom,
    CourseSkill,
    EventType,
    LanguageType,
} from 'types';
import TeacherWelcome from './components';

export const getData = (language: LanguageType): Question => {
    const baseSelect = (
        skillsId?: CourseSkill[],
        languages?: LanguageType[],
        directionsId?: CourseDirection[],
        studyFormsId?: EventType[],
        forWhomId?: CourseForWhom[]
    ) => {
        window.location.href = `/courses/${
            skillsId ? skillsId.join(',') : 'n'
        }/${languages ? languages.join(',') : 'n'}/${
            directionsId ? directionsId.join(',') : 'n'
        }/${studyFormsId ? studyFormsId.join(',') : 'n'}/${
            forWhomId ? forWhomId.join(',') : 'n'
        }/`;
    };

    const firstStep: Question = {
        id: window.crypto.randomUUID(),
        question: 'for-whom',
        title: 'select-course',
    };

    const secondStepChild: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        nextTitle: 'kaspersky-offer-children',
        answer: 'for-child',
        prev: firstStep,
        component: TeacherWelcome,
        nextLink: '/for-kids',
    };

    const secondStepYourself: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        question: 'which-audience',
        answer: 'for-yourself',
        prev: firstStep,
    };

    const secondStepEmployees: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        question: 'which-skills',
        answer: 'for-employees',
        prev: firstStep,
    };

    const secondStepStudents: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'for-students',
        prev: firstStep,
    };

    if (language === LanguageType.English) {
        secondStepStudents.nextLink = '/academy-alliance';
        secondStepStudents.nextTitle = 'students-for';
        secondStepStudents.component = TeacherWelcome;
    } else {
        secondStepStudents.question = 'who-you-are';

        const thirdStepStudentSchoolTeacher: Question = {
            id: window.crypto.randomUUID(),
            title: 'select-course',
            answer: 'school-teacher',
            prev: secondStepStudents,
            nextTitle: 'books-for-teachers',
            component: TeacherWelcome,
            nextLink: '/for-schools-and-colleges',
        };
        const thirdStepStudentUniversityTeacher: Question = {
            id: window.crypto.randomUUID(),
            title: 'select-course',
            answer: 'university-teacher',
            prev: secondStepStudents,
            nextTitle: 'students-for',
            component: TeacherWelcome,
            nextLink: '/academy-alliance',
        };

        secondStepStudents.next = [
            thirdStepStudentSchoolTeacher,
            thirdStepStudentUniversityTeacher,
        ];
    }

    firstStep.next = [
        secondStepChild,
        secondStepYourself,
        secondStepEmployees,
        secondStepStudents,
    ];

    const thirdStepSelfSchool: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'for-school',
        prev: secondStepYourself,
        nextTitle: 'kaspersky-offer-children',
        component: TeacherWelcome,
        nextLink: '/for-kids',
    };
    const thirdStepSelfStudent: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'student',
        prev: secondStepYourself,
        select: () =>
            baseSelect(undefined, undefined, [
                CourseDirection.ForStudentsAndInterns,
            ]),
    };

    const thirdStepSelfBasicUser: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'base-user',
        prev: secondStepYourself,
        select: () =>
            baseSelect(undefined, undefined, [CourseDirection.FreeCourse]),
    };

    const thirdStepSelfItSpecialist: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'it-specialist',
        prev: secondStepYourself,
        select: () =>
            baseSelect(undefined, undefined, [
                CourseDirection.ForItProfessionals,
            ]),
    };
    const thirdStepSelfCsProfessional: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'cs-professional',
        question: 'what-you-want-study',
        prev: secondStepYourself,
    };

    secondStepYourself.next = [
        thirdStepSelfSchool,
        thirdStepSelfStudent,
        thirdStepSelfBasicUser,
        thirdStepSelfItSpecialist,
        thirdStepSelfCsProfessional,
    ];

    const fifthStepSelfCsProfessionalAnalysis: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'malware-analysis',
        prev: thirdStepSelfCsProfessional,
        select: () =>
            baseSelect(undefined, undefined, [CourseDirection.MalwareAnalysis]),
    };

    const fifthStepSelfCsProfessionalIncident: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'incident-response',
        prev: thirdStepSelfCsProfessional,
        select: () =>
            baseSelect(undefined, undefined, [
                CourseDirection.IncidentResponse,
            ]),
    };

    const fifthStepSelfCsProfessionalActiveThreatHunting: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'active-threat-hunting',
        prev: thirdStepSelfCsProfessional,
        select: () =>
            baseSelect(undefined, undefined, [
                CourseDirection.ActiveThreatHunting,
            ]),
    };

    const fifthStepSelfCsProfessionalSafeDevelopment: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'safe-development',
        prev: thirdStepSelfCsProfessional,
        select: () =>
            baseSelect(undefined, undefined, [
                CourseDirection.SecureSoftwareDevelopment,
            ]),
    };

    thirdStepSelfCsProfessional.next = [
        fifthStepSelfCsProfessionalAnalysis,
        fifthStepSelfCsProfessionalIncident,
        fifthStepSelfCsProfessionalActiveThreatHunting,
    ];

    if (language === LanguageType.Russian)
        thirdStepSelfCsProfessional.next = [
            ...thirdStepSelfCsProfessional.next,
            fifthStepSelfCsProfessionalSafeDevelopment,
        ];

    const thirdStepEmployeesTechnical: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'technical',
        question: 'which-topic',
        prev: secondStepEmployees,
    };

    const thirdStepEmployeesCsAwareness: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'cs-awareness',
        question: 'who-you-like-to-train',
        prev: secondStepEmployees,
    };

    secondStepEmployees.next = [
        thirdStepEmployeesTechnical,
        thirdStepEmployeesCsAwareness,
    ];

    const fifthStepEmployeesTechnicalСs: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'cs-for-it',
        prev: thirdStepEmployeesTechnical,
        select: () =>
            baseSelect(undefined, undefined, [
                CourseDirection.ForItProfessionals,
            ]),
    };

    const fifthStepEmployeesTechnicalMAnalysis: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'malware-analysis',
        prev: thirdStepEmployeesTechnical,
        select: () =>
            baseSelect(undefined, undefined, [CourseDirection.MalwareAnalysis]),
    };

    const fifthStepEmployeesTechnicalIncidents: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'incident-response',
        prev: thirdStepEmployeesTechnical,
        select: () =>
            baseSelect(undefined, undefined, [
                CourseDirection.IncidentResponse,
            ]),
    };

    const fifthStepEmployeesTechnicalHunting: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'active-threat-hunting',
        prev: thirdStepEmployeesTechnical,
        select: () =>
            baseSelect(undefined, undefined, [
                CourseDirection.ActiveThreatHunting,
            ]),
    };

    thirdStepEmployeesTechnical.next = [
        fifthStepEmployeesTechnicalСs,
        fifthStepEmployeesTechnicalMAnalysis,
        fifthStepEmployeesTechnicalIncidents,
        fifthStepEmployeesTechnicalHunting,
    ];

    const fifthStepEmployeesTechnicalSafe: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'safe-development',
        prev: thirdStepEmployeesTechnical,
        select: () =>
            baseSelect(undefined, undefined, [
                CourseDirection.SecureSoftwareDevelopment,
            ]),
    };

    if (language === LanguageType.Russian)
        thirdStepEmployeesTechnical.next = [
            ...thirdStepEmployeesTechnical.next,
            fifthStepEmployeesTechnicalSafe,
        ];

    const fifthStepEmployeesTechnicalIndustrial: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'cs-industrial',
        prev: thirdStepEmployeesTechnical,
        select: () =>
            baseSelect([CourseSkill.Technical], undefined, [
                CourseDirection.IndustrialCybersecurity,
            ]),
    };

    const fifthStepEmployeesTechnicalAll: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'cs-all',
        prev: thirdStepEmployeesTechnical,
        select: () => baseSelect([CourseSkill.Technical], undefined, undefined),
    };

    thirdStepEmployeesTechnical.next = [
        ...thirdStepEmployeesTechnical.next,
        fifthStepEmployeesTechnicalIndustrial,
        fifthStepEmployeesTechnicalAll,
    ];

    const fifthStepEmployeesCsAwarenessAll: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'all-employees',
        prev: thirdStepEmployeesCsAwareness,
        select: () =>
            baseSelect([CourseSkill.KnowledgeCyberSecurity], undefined, [
                CourseDirection.ForEmployeesAwareness,
            ]),
    };

    const fifthStepEmployeesCsAwarenessTopManagers: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'top-managers',
        prev: thirdStepEmployeesCsAwareness,
        select: () =>
            baseSelect([CourseSkill.KnowledgeCyberSecurity], undefined, [
                CourseDirection.ForTopManagers,
            ]),
    };

    const fifthStepEmployeesCsAwarenessIndustrial: Question = {
        id: window.crypto.randomUUID(),
        title: 'select-course',
        answer: 'industrial-sys',
        prev: thirdStepEmployeesCsAwareness,
        select: () =>
            baseSelect([CourseSkill.KnowledgeCyberSecurity], undefined, [
                CourseDirection.IndustrialCybersecurity,
            ]),
    };

    thirdStepEmployeesCsAwareness.next = [
        fifthStepEmployeesCsAwarenessAll,
        fifthStepEmployeesCsAwarenessTopManagers,
        fifthStepEmployeesCsAwarenessIndustrial,
    ];

    return firstStep;
};
